import React, { useRef, useCallback, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useUpdateEffect } from "react-use";
import debounce from "lodash/debounce";
import PropTypes from "prop-types";
import { EDIT_EXPENSE_SUCCESS } from "~/store/action_types";
import { editExpense } from "~/store/actions/actions.expenses";
import { addUploadedFile } from "~/store/actions/actions.uploadFile";
import { AttachmentMenu } from "./AttachmentMenu";
import { useFileHandler } from "../Hooks";
import { AttachmentUpload } from "../select_attachments/AttachmentUpload";
import { AttachmentsPicker } from "../select_attachments/menu/AttachmentsPicker";

export const ExpenseAttachmentPicker = ({ expenseId, canEdit, projectId, attachmentsCount, onDeleteAttachment }) => {
  const dispatch = useDispatch();
  const [filesToAttach, setFilesToAttach] = useState([]);

  const handleUploadFile = useCallback(
    ({ files }) => {
      files.map((file) => {
        dispatch(addUploadedFile(file));
        setFilesToAttach((prev) => [file, ...prev]);
      });
    },
    [dispatch]
  );

  const debouncedAttach = useRef(
    debounce(async (filesToAttach) => {
      const codes = filesToAttach.map((file) => file.code);
      const attributes = {
        attach_uploaded_files: codes,
      };
      const { type } = await dispatch(editExpense(projectId, expenseId, attributes));
      if (type === EDIT_EXPENSE_SUCCESS) {
        setFilesToAttach([]);
      }
    }, 1000)
  );

  const { onAttachFromDropbox, onAttachFromGoogleDrive, onUpload, onRetryUpload, onCancelUpload, uploads, uploading } =
    useFileHandler({
      context: `expense-${expenseId}`,
      successCallback: handleUploadFile,
      clearOnUnmount: false,
    });

  useUpdateEffect(() => {
    if (filesToAttach.length > 0 && !uploading) {
      debouncedAttach.current(filesToAttach);
    }
  }, [filesToAttach, uploading]);

  const fileInputRef = useRef(null);

  const uploadsList = useMemo(() => {
    return uploads ? Object.values(uploads) : [];
  }, [uploads]);

  const handleOpenFileWindow = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const uploadInProgress = Boolean(filesToAttach.length) || (Boolean(uploadsList.length) && uploading);

  const showAttachmentMenuList = attachmentsCount > 0 || Boolean(filesToAttach.length) || Boolean(uploadsList.length);

  return (
    <>
      {canEdit && !showAttachmentMenuList ? (
        <AttachmentsPicker
          openFileWindow={handleOpenFileWindow}
          onAttachFromDropbox={onAttachFromDropbox}
          onAttachFromGoogleDrive={onAttachFromGoogleDrive}
        />
      ) : null}
      {showAttachmentMenuList ? (
        <AttachmentMenu
          open={uploadInProgress}
          parentId={expenseId}
          parentType="Expense"
          attachmentsCount={attachmentsCount}
          projectId={projectId}
          canEdit={canEdit}
          openFileWindow={handleOpenFileWindow}
          onAttachFromDropbox={onAttachFromDropbox}
          onAttachFromGoogleDrive={onAttachFromGoogleDrive}
          onRetry={onRetryUpload}
          onRemove={onCancelUpload}
          uploadsList={uploadsList}
          filesToAttach={filesToAttach}
          onDeleteAttachment={onDeleteAttachment}
        />
      ) : null}
      <AttachmentUpload
        ref={fileInputRef}
        multiple
        onUpload={onUpload}
      />
    </>
  );
};

ExpenseAttachmentPicker.propTypes = {
  expenseId: PropTypes.number.isRequired,
  projectId: PropTypes.number.isRequired,
  canEdit: PropTypes.bool,
  attachmentsCount: PropTypes.number,
  onDeleteAttachment: PropTypes.func,
};
